/**
 * SearchPage - Entity Search and Expert Finder
 *
 * Surfaces the entitySearch and findExpertsBySpecializations resolvers.
 * Provides a dedicated search interface for admin/architect users.
 *
 * Route: /admin/search
 *
 * DRR-233 Track C: Surfacing DEP resolvers (#960, #961)
 * FERP Principles:
 * - Domain-agnostic: scope strategy inferred from entity type
 * - DRY: one query for all subgraphs and entity types
 * - Constellation-first: person/role entities scoped to organizational boundary
 */

import React, { useState, useCallback } from 'react';
import {
  Container,
  Title,
  Group,
  Badge,
  Text,
  ThemeIcon,
  Tabs,
  TextInput,
  Select,
  MultiSelect,
  Button,
  Table,
  Card,
  Stack,
  SimpleGrid,
  LoadingOverlay,
  Alert,
  Paper,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import {
  IconSearch,
  IconUserSearch,
  IconAlertCircle,
  IconUsers,
  IconBriefcase,
} from '@tabler/icons-react';
import { useLazyQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';

import { useAuth } from '@/auth/AuthContext';

// =============================================================================
// GraphQL Operations
// =============================================================================

const ENTITY_SEARCH = gql`
  query EntitySearch($input: EntitySearchInput!) {
    entitySearch(input: $input) {
      id
      displayName
      secondary
      subgraph
      entityType
    }
  }
`;

const FIND_EXPERTS = gql`
  query FindExpertsBySpecializations(
    $tenant: String!
    $specializations: [String!]!
    $complianceFrameworks: [String!]!
  ) {
    findExpertsBySpecializations(
      tenant: $tenant
      specializations: $specializations
      complianceFrameworks: $complianceFrameworks
    ) {
      id
      name
      actorType
      email
      roles
      permissions
      metadata
      tenant
      createdAt
      updatedAt
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

interface EntitySearchResult {
  id: string;
  displayName: string;
  secondary: string | null;
  subgraph: string;
  entityType: string;
}

interface Expert {
  id: string;
  name: string;
  actorType: string;
  email: string | null;
  roles: string[];
  permissions: string[];
  metadata: Record<string, unknown>;
  tenant: string;
  createdAt: string;
  updatedAt: string;
}

// =============================================================================
// Constants
// =============================================================================

const SUBGRAPHS = [
  { value: 'core', label: 'Core (Primitives)' },
  { value: 'fhir', label: 'FHIR (Healthcare)' },
  { value: 'payroll', label: 'Payroll' },
  { value: 'erp', label: 'ERP (Accounting)' },
];

const ENTITY_TYPES = [
  { value: 'Actor', label: 'Actor' },
  { value: 'Team', label: 'Team' },
  { value: 'Role', label: 'Role' },
  { value: 'Workflow', label: 'Workflow' },
  { value: 'Task', label: 'Task' },
  { value: 'Document', label: 'Document' },
  { value: 'FhirPatient', label: 'FHIR Patient' },
  { value: 'FhirPractitioner', label: 'FHIR Practitioner' },
];

const SPECIALIZATIONS = [
  { value: 'medication-management', label: 'Medication Management' },
  { value: 'wound-care', label: 'Wound Care' },
  { value: 'dementia-care', label: 'Dementia Care' },
  { value: 'palliative-care', label: 'Palliative Care' },
  { value: 'behavioral-health', label: 'Behavioral Health' },
  { value: 'physical-therapy', label: 'Physical Therapy' },
  { value: 'occupational-therapy', label: 'Occupational Therapy' },
  { value: 'speech-therapy', label: 'Speech Therapy' },
];

const COMPLIANCE_FRAMEWORKS = [
  { value: 'MN_144G', label: 'MN 144G (ALF)' },
  { value: 'MN_245D', label: 'MN 245D (Disability Services)' },
  { value: 'CMS_F_TAGS', label: 'CMS F-Tags' },
  { value: 'OSHA_1910', label: 'OSHA 1910 (General Industry)' },
  { value: 'HIPAA', label: 'HIPAA Privacy' },
  { value: 'SOX', label: 'SOX Compliance' },
];

// =============================================================================
// Component
// =============================================================================

export function SearchPage() {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';

  // Entity Search State
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSubgraph, setSelectedSubgraph] = useState<string>('core');
  const [selectedEntityType, setSelectedEntityType] = useState<string>('Actor');
  const [debouncedQuery] = useDebouncedValue(searchQuery, 300);

  // Expert Finder State
  const [selectedSpecializations, setSelectedSpecializations] = useState<string[]>([]);
  const [selectedFrameworks, setSelectedFrameworks] = useState<string[]>([]);

  // GraphQL Queries
  const [executeEntitySearch, { data: entityData, loading: entityLoading, error: entityError }] =
    useLazyQuery<{ entitySearch: EntitySearchResult[] }>(ENTITY_SEARCH);

  const [executeExpertSearch, { data: expertData, loading: expertLoading, error: expertError }] =
    useLazyQuery<{ findExpertsBySpecializations: Expert[] }>(FIND_EXPERTS);

  // Handlers
  const handleEntitySearch = useCallback(() => {
    if (debouncedQuery.length >= 2) {
      executeEntitySearch({
        variables: {
          input: {
            subgraph: selectedSubgraph,
            entityType: selectedEntityType,
            query: debouncedQuery,
            limit: 50,
          },
        },
      });
    }
  }, [debouncedQuery, selectedSubgraph, selectedEntityType, executeEntitySearch]);

  const handleExpertSearch = useCallback(() => {
    if (selectedSpecializations.length > 0) {
      executeExpertSearch({
        variables: {
          tenant: tenantId,
          specializations: selectedSpecializations,
          complianceFrameworks: selectedFrameworks,
        },
      });
    }
  }, [tenantId, selectedSpecializations, selectedFrameworks, executeExpertSearch]);

  // Auto-search on query change
  React.useEffect(() => {
    handleEntitySearch();
  }, [debouncedQuery, handleEntitySearch]);

  return (
    <Container size="xl" py="md">
      <Group justify="space-between" mb="lg">
        <Group gap="sm">
          <ThemeIcon size="lg" variant="light" color="blue">
            <IconSearch size={20} />
          </ThemeIcon>
          <div>
            <Title order={3}>Search</Title>
            <Text size="sm" c="dimmed">
              Cross-subgraph entity search and expert finder
            </Text>
          </div>
        </Group>
        {tenant && (
          <Badge variant="light" color="gray">
            {tenant.name}
          </Badge>
        )}
      </Group>

      <Tabs defaultValue="entity-search">
        <Tabs.List mb="md">
          <Tabs.Tab value="entity-search" leftSection={<IconSearch size={16} />}>
            Entity Search
          </Tabs.Tab>
          <Tabs.Tab value="expert-finder" leftSection={<IconUserSearch size={16} />}>
            Expert Finder
          </Tabs.Tab>
        </Tabs.List>

        {/* Entity Search Tab */}
        <Tabs.Panel value="entity-search">
          <Paper p="md" withBorder mb="md">
            <SimpleGrid cols={{ base: 1, sm: 3 }} mb="md">
              <TextInput
                label="Search Query"
                placeholder="Enter search term..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.currentTarget.value)}
                leftSection={<IconSearch size={16} />}
              />
              <Select
                label="Subgraph"
                data={SUBGRAPHS}
                value={selectedSubgraph}
                onChange={(v) => setSelectedSubgraph(v ?? 'core')}
              />
              <Select
                label="Entity Type"
                data={ENTITY_TYPES}
                value={selectedEntityType}
                onChange={(v) => setSelectedEntityType(v ?? 'Actor')}
              />
            </SimpleGrid>
            <Text size="xs" c="dimmed">
              Scope is inferred from entity type: person/role entities are constellation-scoped,
              others are tenant-scoped.
            </Text>
          </Paper>

          <Card withBorder pos="relative" mih={200}>
            <LoadingOverlay visible={entityLoading} />

            {entityError && (
              <Alert icon={<IconAlertCircle size={16} />} color="red" mb="md">
                {entityError.message}
              </Alert>
            )}

            {entityData?.entitySearch && entityData.entitySearch.length > 0 ? (
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>ID</Table.Th>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Type</Table.Th>
                    <Table.Th>Subgraph</Table.Th>
                    <Table.Th>Secondary</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {entityData.entitySearch.map((result) => (
                    <Table.Tr key={result.id}>
                      <Table.Td>
                        <Text size="sm" ff="monospace">
                          {result.id}
                        </Text>
                      </Table.Td>
                      <Table.Td>{result.displayName}</Table.Td>
                      <Table.Td>
                        <Badge variant="light">{result.entityType}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Badge variant="outline">{result.subgraph}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {result.secondary ?? '-'}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            ) : (
              <Stack align="center" py="xl">
                <IconSearch size={48} color="gray" opacity={0.5} />
                <Text c="dimmed">
                  {searchQuery.length >= 2
                    ? 'No results found'
                    : 'Enter at least 2 characters to search'}
                </Text>
              </Stack>
            )}
          </Card>
        </Tabs.Panel>

        {/* Expert Finder Tab */}
        <Tabs.Panel value="expert-finder">
          <Paper p="md" withBorder mb="md">
            <SimpleGrid cols={{ base: 1, sm: 2 }} mb="md">
              <MultiSelect
                label="Specializations"
                placeholder="Select specializations..."
                data={SPECIALIZATIONS}
                value={selectedSpecializations}
                onChange={setSelectedSpecializations}
                leftSection={<IconBriefcase size={16} />}
                searchable
                clearable
              />
              <MultiSelect
                label="Compliance Frameworks"
                placeholder="Optional: filter by compliance..."
                data={COMPLIANCE_FRAMEWORKS}
                value={selectedFrameworks}
                onChange={setSelectedFrameworks}
                leftSection={<IconUsers size={16} />}
                searchable
                clearable
              />
            </SimpleGrid>
            <Button onClick={handleExpertSearch} disabled={selectedSpecializations.length === 0}>
              Find Experts
            </Button>
          </Paper>

          <Card withBorder pos="relative" mih={200}>
            <LoadingOverlay visible={expertLoading} />

            {expertError && (
              <Alert icon={<IconAlertCircle size={16} />} color="red" mb="md">
                {expertError.message}
              </Alert>
            )}

            {expertData?.findExpertsBySpecializations &&
            expertData.findExpertsBySpecializations.length > 0 ? (
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Type</Table.Th>
                    <Table.Th>Email</Table.Th>
                    <Table.Th>Roles</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {expertData.findExpertsBySpecializations.map((expert) => (
                    <Table.Tr key={expert.id}>
                      <Table.Td>{expert.name}</Table.Td>
                      <Table.Td>
                        <Badge variant="light">{expert.actorType}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{expert.email ?? '-'}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          {expert.roles.slice(0, 3).map((role) => (
                            <Badge key={role} size="xs" variant="outline">
                              {role}
                            </Badge>
                          ))}
                          {expert.roles.length > 3 && (
                            <Badge size="xs" variant="filled">
                              +{expert.roles.length - 3}
                            </Badge>
                          )}
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            ) : (
              <Stack align="center" py="xl">
                <IconUserSearch size={48} color="gray" opacity={0.5} />
                <Text c="dimmed">
                  {selectedSpecializations.length > 0
                    ? 'No experts found matching criteria'
                    : 'Select specializations to find experts'}
                </Text>
              </Stack>
            )}
          </Card>
        </Tabs.Panel>
      </Tabs>
    </Container>
  );
}

export default SearchPage;
